import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState, useCallback, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'

import { AppButton, Checkbox, PasswordCriteria } from '@/components'
import { BaseView, BaseText } from '@/components/base'
import { TextInputForm } from '@/components/form'
import { inputLimits } from '@/constants/inputLimits'
import { useSetupPassword } from '@/domain'
import { usePasswordCriteriaControl } from '@/hooks'
import { useTelegramLink } from '@/hooks/useTelegramLink'
import { ICreatePasswordSchema, useCreatePasswordSchema } from '@/schemas'
import { useAuthScopedStore } from '@/store/scoped'
import { useAppAnalytics } from '@/services/analytics'

export const SignUpCreatePasswordForm = () => {
  const [isValidForm, setIsValidForm] = useState(true)
  const [isTermsAccepted, setIsTermsAccepted] = useState(false)
  // Store password ref for telegram linking (password is NOT persisted, only in component)
  const passwordRef = useRef('')

  const { t } = useTranslation()
  const navigate = useNavigate()

  const verifyToken = useAuthScopedStore(
    (state) => state.signUpForm.verifyToken,
  )
  const signUpForm = useAuthScopedStore((state) => state.signUpForm)
  const email = useAuthScopedStore((state) => state.email)
  const phone = useAuthScopedStore((state) => state.phone)

  const {
    allCriteriaMet,
    setAllCriteriaMet,
    checkCriteriaOnBlur,
    showErrorState,
  } = usePasswordCriteriaControl()
  const schema = useCreatePasswordSchema()
  const { trackEvent, events } = useAppAnalytics()
  const { getCanLink, linkWithAccount } = useTelegramLink()

  const { mutateAsync: setupPassword, isPending } = useSetupPassword({
    onSuccess: () => {
      trackEvent(events.ACCOUNT_CREATED, {
        referral_used: false, // TODO: Retrieve actual referral status from store/storage
      })

      // After successful password setup, try to link Telegram account if applicable
      // Password is passed directly from component state - never persisted
      // This happens BEFORE profile setup as per requirements
      // Note: Use getCanLink() to get current value, avoiding stale closure issues
      const currentCanLink = getCanLink()
      if (currentCanLink && passwordRef.current) {
        // Construct phone number in E.164 format if available
        const phoneNumber = phone ? `${phone.countryCode}${phone.number}` : undefined

        // Link in background - don't block navigation to profile setup
        linkWithAccount({
          email: email || undefined,
          phoneNumber,
          password: passwordRef.current,
        }).catch(() => {
          // Error already logged and toast shown by hook
          // Don't block user from proceeding
        })
      }

      navigate('/setup-profile')
    },
    onError: ({ errorMessage }) => {
      if (errorMessage) {
        toast.error(errorMessage)
      }
    },
  })

  const {
    control,
    handleSubmit,
    watch,
    formState,
    setFocus,
    trigger,
    setValue,
  } = useForm<ICreatePasswordSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  })

  const isFormReadyToSubmit =
    formState.isDirty && isValidForm && allCriteriaMet && isTermsAccepted

  const handleSubmitForm = useCallback(
    async (formData: ICreatePasswordSchema) => {
      if (!verifyToken) {
        toast.error('Verification token is missing')
        return
      }

      // Store password in ref for telegram linking (never persisted)
      passwordRef.current = formData.password

      await setupPassword({
        password: formData.password,
        verify_token: verifyToken,
      })
    },
    [setupPassword, verifyToken],
  )

  useEffect(() => {
    setIsValidForm(formState.isValid)
  }, [formState.isValid])

  return (
    <BaseView gap={36} className="flex-1 justify-start">
      {/* Header */}

      {/* Password Form */}
      <BaseView gap={20} className="w-full max-w-md">
        <BaseView gap={12}>
          <TextInputForm
            control={control}
            name="password"
            label={t('form:labels.enterPassword')}
            type="password"
            maxLength={inputLimits.password.max}
            onBlur={checkCriteriaOnBlur}
            onChange={(e) => {
              const value = e.target.value
              setValue('password', value)

              if (value && watch('confirmPassword').length >= 8) {
                trigger('confirmPassword')
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setFocus('confirmPassword')
              }
            }}
          />
          <TextInputForm
            control={control}
            name="confirmPassword"
            label={t('form:labels.confirmPassword')}
            type="password"
            maxLength={inputLimits.password.max}
            onBlur={() =>
              watch('confirmPassword').length > 1
                ? trigger('confirmPassword')
                : undefined
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit(handleSubmitForm)()
              }
            }}
          />
        </BaseView>

        <PasswordCriteria
          password={watch('password')}
          setAllCriteriaMet={setAllCriteriaMet}
          showErrorState={showErrorState}
        />
      </BaseView>

      {/* Terms and Submit */}
      <BaseView gap={26} className="w-full max-w-md">
        <div className="flex items-start gap-3">
          <Checkbox
            id="terms"
            checked={isTermsAccepted}
            onChange={setIsTermsAccepted}
            size={24}
            className="mt-1 flex-shrink-0"
          />
          <label htmlFor="terms" className="text-sm text-whitecursor-pointer">
            {t('screens:signup.agreeWithOur')}{' '}
            <Link
              to="https://kemapp.io/policies"
              className="text-white underline font-semibold text-nowrap"
            >
              {t('screens:signup.termsOfService')}
            </Link>
          </label>
        </div>

        <AppButton
          label={t('form:actions.next')}
          disabled={!isFormReadyToSubmit}
          isLoading={isPending}
          onClick={handleSubmit(handleSubmitForm)}
          className="w-full"
        />
      </BaseView>
    </BaseView>
  )
}

export default SignUpCreatePasswordForm
